import { format_price_with_currency } from './components/Helpers';

export function printingPickerSnapshot() {
  return ['deck', 'sideboard', 'maybeboard', 'format', 'subformat', 'specific_cards', 'commander', 'commander_alt', 'companion'].map(function(name) {
    var element = document.getElementById('deck_input_' + name);
    return name + '=' + (element && element.type === 'checkbox' ? element.checked : (element?.value || ''));
  }).join('\u001f');
}

export function applyPrintingChoices(payload, choices) {
  var hasOwn = Object.prototype.hasOwnProperty;
  Object.keys(payload.documents).forEach(function(field) {
    var text = payload.documents[field];
    payload.entries.filter(function(entry) { return entry.source_field === field; }).sort(function(a, b) {
      return b.line_number - a.line_number;
    }).forEach(function(entry) {
      if (entry.skipped) return;
      if (!hasOwn.call(choices, entry.key)) return;
      var uuid = choices[entry.key];
      if (!uuid) throw new Error('Missing printing selection');
      if (entry.option_uuids.indexOf(uuid) === -1) throw new Error('Invalid printing selection');
      var option = payload.options[uuid];
      var lines = text.split(/(\r\n|\n|\r)/);
      var lineIndex = entry.line_number * 2;
      var line = lines[lineIndex] || '';
      lines[lineIndex] = line.slice(0, entry.identity_start) + option.display_identity + line.slice(entry.identity_end);
      text = lines.join('');
    });
    var textarea = document.getElementById('deck_input_' + field);
    if (textarea) textarea.value = text;
  });
}

export function applyRoleChoices(payload, choices) {
  var hasOwn = Object.prototype.hasOwnProperty;
  Object.keys(payload.roles || {}).forEach(function(role) {
    var control = document.getElementById('deck_input_' + role);
    var resolved = payload.roles[role];
    if (!control || !resolved) return;
    if (resolved.entry_key) {
      if (!hasOwn.call(choices, resolved.entry_key)) return;
      var bound = payload.entries.find(function(entry) { return entry.key === resolved.entry_key; });
      var option = bound && payload.options[choices[bound.key]];
      if (option) control.value = option.display_identity;
    }
  });
}

const PRINTING_PICKER_OPTION_PAGE_SIZE = 40;

export class DeckPrintingPicker {
  constructor(launcher) {
    this.launcher = launcher;
    this.modalElement = document.getElementById('deck-printing-picker');
    this.endpoint = launcher?.dataset.endpoint || this.modalElement?.dataset.endpoint;
    this.modal = window.bootstrap.Modal.getOrCreateInstance(this.modalElement, { backdrop: 'static' });
    this.queue = document.getElementById('deck-printing-picker-queue');
    this.decided = document.getElementById('deck-printing-picker-decided');
    this.alert = document.getElementById('deck-printing-picker-alert');
    this.loading = document.getElementById('deck-printing-picker-loading');
    this.progressMeter = this.modalElement?.querySelector('.deck-printing-picker-progress');
    this.progress = document.getElementById('deck-printing-picker-progress');
    this.progressTop = document.getElementById('deck-printing-picker-progress-top');
    this.progressBar = document.getElementById('deck-printing-picker-progress-bar');
    this.applyButton = document.getElementById('deck-printing-picker-apply');
    this.payload = null;
    this.choices = {};
    this.openRequestId = 0;
    this.singleCardMode = false;
    launcher?.addEventListener('click', this.open.bind(this));
    this.applyButton.addEventListener('click', this.apply.bind(this));
  }

  formData() {
    var data = {};
    ['deck', 'sideboard', 'maybeboard', 'commander', 'commander_alt', 'companion'].forEach(function(name) {
      data[name] = document.getElementById('deck_input_' + name)?.value || '';
    });
    return data;
  }

  async open(options) {
    if (options && typeof options.preventDefault === 'function') {
      options.preventDefault();
      options = {};
    }
    options = options || {};
    var requestId = ++this.openRequestId;
    this.singleCardMode = Boolean(options.singleCardMode);
    var previewTarget = options.previewRow ? previewRowTarget(options.previewRow) : null;
    this.modal.show();
    this.raiseModalStack();
    this.setError('');
    this.payload = null;
    this.choices = {};
    this.applyButton.disabled = true;
    this.resetProgress();
    this.loading.classList.remove('d-none');
    this.queue.innerHTML = '';
    this.decided.innerHTML = '';
    var synchronized = await (window.syncEditableDeckPreviewBeforeFormAction?.() || Promise.resolve(true));
    if (requestId !== this.openRequestId) return;
    if (!synchronized) {
      this.loading.classList.add('d-none');
      return this.setError('Finish resolving the current preview update, then try again.');
    }
    this.snapshot = printingPickerSnapshot();
    try {
      var response = await fetch(this.endpoint, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
        body: JSON.stringify(this.formData())
      });
      var body = await response.json();
      if (requestId !== this.openRequestId) return;
      if (!response.ok) throw new Error(body.error?.message || 'Unable to load card printings.');
      if (previewTarget) body = payloadForPreviewTarget(body, previewTarget);
      this.payload = body;
      this.choices = {};
      this.render();
    } catch (error) {
      if (requestId !== this.openRequestId) return;
      this.setError(error.message);
      if (window.Bugsnag && !error.message.match(/could not be resolved|exceeds|No matching deck entry/)) window.Bugsnag.notify(error);
    } finally {
      if (requestId === this.openRequestId) this.loading.classList.add('d-none');
    }
  }

  openForPreviewRow(row) {
    return this.open({ previewRow: row, singleCardMode: true });
  }

  render() {
    this.queue.innerHTML = '';
    this.updateProgressVisibility();
    this.decided.innerHTML = this.singleCardMode ? '' : '<h3 class="h5">Selected and single-printing cards</h3>';
    var openCount = 0;
    this.payload.entries.forEach((entry) => {
      var chosen = this.choices[entry.key];
      var container = document.createElement('article');
      if (entry.skipped) {
        container.className = 'deck-printing-picker-decided-row deck-printing-picker-warning-row';
        this.renderSkipped(container, entry);
        this.decided.appendChild(container);
      } else if (chosen) {
        container.className = 'deck-printing-picker-decided-row';
        this.renderDecided(container, entry, chosen);
        this.decided.appendChild(container);
      } else if (entry.customizable || this.singleCardCurrentOnlyEntry(entry)) {
        container.className = 'deck-printing-picker-card';
        openCount += 1;
        this.renderCard(container, entry);
        this.queue.appendChild(container);
      } else {
        container.className = 'deck-printing-picker-decided-row';
        this.renderDecided(container, entry, entry.current_uuid);
        this.decided.appendChild(container);
      }
    });
    if (openCount === 0) {
      var empty = document.createElement('p');
      empty.className = 'deck-printing-picker-empty';
      empty.textContent = 'All customizable cards have selections.';
      this.queue.appendChild(empty);
    }
    var customizable = this.payload.entries.filter(function(entry) { return entry.customizable; });
    var decidedCount = customizable.filter((entry) => this.choices[entry.key]).length;
    var progressText = decidedCount + ' of ' + customizable.length + ' customizable cards selected';
    this.progress.textContent = progressText;
    if (this.progressTop) this.progressTop.textContent = progressText;
    if (this.progressBar) {
      var pct = customizable.length ? Math.round((decidedCount / customizable.length) * 100) : 100;
      this.progressBar.style.width = pct + '%';
      this.progressBar.setAttribute('aria-valuenow', pct);
    }
    this.applyButton.disabled = false;
    window.bindPopover?.();
  }

  renderSkipped(container, entry) {
    container.innerHTML = '<span><strong></strong> <small></small></span>';
    container.querySelector('strong').textContent = quantityLabel(entry) + ' ' + entry.display_name;
    container.querySelector('small').textContent = entry.warning || 'This card could not be resolved and was skipped.';
  }

  renderDecided(container, entry, uuid) {
    var option = this.payload.options[uuid];
    container.innerHTML = '<span><strong><span class="deck-printing-picker-decided-quantity"></span> <span class="deck-printing-picker-decided-card-name"></span></strong> <small></small><small class="deck-printing-picker-limit-notice d-none"></small></span>' + (entry.customizable ? '<button type="button" class="btn btn-link btn-sm">Change</button>' : '');
    container.querySelector('.deck-printing-picker-decided-quantity').textContent = quantityLabel(entry);
    var cardName = container.querySelector('.deck-printing-picker-decided-card-name');
    cardName.textContent = entry.display_name;
    setPopoverAttributes(cardName, option);
    container.querySelector('small').textContent = option ? optionLabel(option) + (entry.compatibility_only_current && entry.option_uuids.length === 1 && !entry.option_limit_truncated ? ' - Current printing; no eligible alternatives' : '') : 'No returned printings';
    this.renderLimitNotice(container.querySelector('.deck-printing-picker-limit-notice'), entry);
    container.querySelector('button')?.addEventListener('click', () => {
      delete this.choices[entry.key];
      this.render();
      this.queue.querySelector('.deck-printing-picker-card button')?.focus();
    });
  }

  renderCard(container, entry) {
    container.innerHTML = '<div class="deck-printing-picker-card-header"><h3 class="h5"></h3><button type="button" class="btn btn-outline-secondary btn-sm">Keep Current</button></div><div class="deck-printing-picker-controls"><label>Search printings<input class="form-control form-control-sm" type="search"></label><span class="deck-printing-picker-sort-control">Sort <select class="form-select form-select-sm deck-printing-picker-sort-type" aria-label="Sort type"><option value="paper-price" selected>Tabletop Price</option><option value="online-price">Magic Online Price</option><option value="release-date">Release Date</option></select> : <select class="form-select form-select-sm deck-printing-picker-sort-direction" aria-label="Sort direction"><option value="auto" selected>Auto</option><option value="asc">Asc</option><option value="desc">Desc</option></select></span></div><div class="deck-printing-picker-option-status" aria-live="polite"></div><p class="deck-printing-picker-limit-notice d-none"></p><div class="deck-printing-picker-options" role="radiogroup"></div><button type="button" class="btn btn-outline-secondary btn-sm deck-printing-picker-show-more d-none">Show more</button>';
    container.querySelector('h3').textContent = quantityLabel(entry) + ' ' + entry.display_name;
    container.querySelector('button').addEventListener('click', (event) => this.choose(entry, entry.current_uuid, event.currentTarget));
    var optionsContainer = container.querySelector('.deck-printing-picker-options');
    var status = container.querySelector('.deck-printing-picker-option-status');
    var limitNotice = container.querySelector('.deck-printing-picker-limit-notice');
    var showMore = container.querySelector('.deck-printing-picker-show-more');
    var search = container.querySelector('input');
    var sortType = container.querySelector('.deck-printing-picker-sort-type');
    var sortDirection = container.querySelector('.deck-printing-picker-sort-direction');
    var visibleLimit = PRINTING_PICKER_OPTION_PAGE_SIZE;
    var renderOptions = () => {
      var query = search.value.toLowerCase();
      var sortedOptions = sortOptions(
        entry.option_uuids.map((uuid) => {
          var option = this.payload.options[uuid];
          return option && Object.assign({ uuid: uuid }, option);
        }).filter(Boolean),
        sortType.value,
        sortDirection.value
      );
      var matchingOptions = sortedOptions.filter(function(option) {
        var searchable = [optionTitle(option), option.display_identity, optionLabel(option), priceLabel(option, 'online-price')].join(' ').toLowerCase();
        return !query || searchable.includes(query);
      });
      var visibleOptions = matchingOptions.slice(0, visibleLimit);
      optionsContainer.innerHTML = '';
      visibleOptions.forEach((option) => {
        var button = document.createElement('button');
        var isCurrent = option.uuid === entry.current_uuid;
        button.type = 'button'; button.className = 'deck-printing-picker-option' + (isCurrent ? ' is-current' : ''); button.setAttribute('role', 'radio'); button.setAttribute('aria-checked', 'false');
        button.innerHTML = '<div class="deck-printing-picker-option-image"><img class="deck-visual-card-image" loading="lazy"></div><span class="deck-printing-picker-option-body"><strong class="deck-visual-card-title"></strong><small class="deck-printing-picker-option-tags"></small><small class="deck-visual-card-price"></small></span>';
        button.querySelector('img').src = option.image_url || ''; button.querySelector('img').alt = optionTitle(option) + ' card art';
        button.querySelector('strong').textContent = optionTitle(option);
        button.querySelector('.deck-printing-picker-option-tags').textContent = optionTags(option);
        button.querySelector('.deck-visual-card-price').textContent = priceLabel(option, sortType.value);
        button.addEventListener('click', (event) => this.choose(entry, option.uuid, event.currentTarget));
        optionsContainer.appendChild(button);
      });
      if (!optionsContainer.children.length) {
        optionsContainer.innerHTML = '<p class="deck-printing-picker-empty">No returned printings match this search.</p>';
      }
      var returnedLabel = entry.option_limit_truncated ? ' returned' : '';
      var matchLabel = query ? ' matching' : '';
      status.textContent = matchingOptions.length ?
        'Showing ' + visibleOptions.length + ' of ' + matchingOptions.length + returnedLabel + matchLabel + ' printings' :
        'Showing 0 of 0' + returnedLabel + matchLabel + ' printings';
      this.renderLimitNotice(limitNotice, entry);
      showMore.classList.toggle('d-none', visibleOptions.length >= matchingOptions.length);
    };
    renderOptions();
    search.addEventListener('input', () => {
      visibleLimit = PRINTING_PICKER_OPTION_PAGE_SIZE;
      renderOptions();
    });
    sortType.addEventListener('change', () => {
      visibleLimit = PRINTING_PICKER_OPTION_PAGE_SIZE;
      renderOptions();
    });
    sortDirection.addEventListener('change', () => {
      visibleLimit = PRINTING_PICKER_OPTION_PAGE_SIZE;
      renderOptions();
    });
    showMore.addEventListener('click', () => {
      visibleLimit += PRINTING_PICKER_OPTION_PAGE_SIZE;
      renderOptions();
    });
  }

  limitNoticeText(entry) {
    if (!entry.option_limit_truncated) return '';
    var eligibleCount = Number(entry.normal_eligible_count ?? 0);
    var returnedCount = Number(entry.normal_option_count ?? entry.option_uuids.length ?? 0);
    return entry.hard_limit_trimmed ?
      'Showing ' + returnedCount + ' of ' + eligibleCount + ' eligible printings; refine your search to see more options.' :
      'Showing ' + returnedCount + ' of ' + eligibleCount + ' eligible printings; narrow the search to see more options.';
  }

  renderLimitNotice(element, entry) {
    var notice = this.limitNoticeText(entry);
    element.textContent = notice;
    element.classList.toggle('d-none', !notice);
  }

  singleCardCurrentOnlyEntry(entry) {
    return this.singleCardMode &&
      entry.current_uuid &&
      entry.option_uuids?.length === 1 &&
      entry.option_uuids[0] === entry.current_uuid;
  }

  choose(entry, uuid, sourceElement) {
    if (entry.option_uuids.indexOf(uuid) === -1) return;
    if (this.singleCardMode) {
      if (this.singleCardCurrentOnlyEntry(entry) && uuid === entry.current_uuid) {
        this.modal.hide();
        return;
      }
      this.choices[entry.key] = uuid;
      this.apply();
      return;
    }
    var card = sourceElement?.closest('.deck-printing-picker-card');
    if (!card) {
      this.choices[entry.key] = uuid;
      this.render();
      return;
    }
    card.classList.add('is-leaving');
    window.setTimeout(() => {
      this.choices[entry.key] = uuid;
      this.render();
    }, 220);
  }

  async apply() {
    if (!this.payload) return this.setError('Load card printings before applying.');
    if (printingPickerSnapshot() !== this.snapshot) return this.setError('The deck changed while the picker was open. Close and reload the picker before applying.');
    this.applyButton.disabled = true;
    var originals = captureFormState();
    try {
      if (Object.keys(this.choices).length) {
        applyPrintingChoices(this.payload, this.choices);
        applyRoleChoices(this.payload, this.choices);
        var specific = document.getElementById('deck_input_specific_cards');
        if (specific) specific.checked = true;
        ['deck', 'sideboard', 'maybeboard'].forEach(function(field) {
          var textarea = document.getElementById('deck_input_' + field);
          if (textarea) textarea.dataset.specificCardText = textarea.value;
        });
        var updated = await window.requestDeckPreview();
        if (!updated) throw new Error('The printings were applied, but the preview could not be refreshed. Review the decklist before saving.');
      }
      this.modal.hide();
    } catch (error) {
      restoreFormState(originals);
      this.setError(error.message);
      this.applyButton.disabled = false;
    }
  }

  setError(message) {
    this.alert.textContent = message || '';
    this.alert.classList.toggle('d-none', !message);
  }

  resetProgress() {
    this.updateProgressVisibility();
    this.progress.textContent = 'Loading card printings';
    if (this.progressTop) this.progressTop.textContent = 'Loading card printings';
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
      this.progressBar.setAttribute('aria-valuenow', '0');
    }
  }

  updateProgressVisibility() {
    this.progressMeter?.classList.toggle('d-none', this.singleCardMode);
    this.progress?.classList.toggle('d-none', this.singleCardMode);
  }

  raiseModalStack() {
    this.modalElement.style.zIndex = '30010';
    document.querySelectorAll('.modal-backdrop').forEach(function(backdrop) {
      backdrop.style.zIndex = '30000';
    });
  }
}

function optionLabel(option) {
  return [option.set, option.collector_number && '#' + option.collector_number, option.finish_label, ...(option.tags || []), formattedPrice(option.price, 'paper')].filter(Boolean).join(' | ');
}

export function captureFormState() {
  var fields = ['deck', 'sideboard', 'maybeboard', 'commander', 'commander_alt', 'companion'];
  var state = { fields: {}, specificCardsChecked: null };
  fields.forEach(function(field) {
    var element = document.getElementById('deck_input_' + field);
    if (!element) return;
    state.fields[field] = {
      value: element.value,
      specificCardText: element.dataset.specificCardText
    };
  });
  var specific = document.getElementById('deck_input_specific_cards');
  if (specific) state.specificCardsChecked = specific.checked;
  return state;
}

export function restoreFormState(state) {
  Object.keys(state.fields).forEach(function(field) {
    var element = document.getElementById('deck_input_' + field);
    if (!element) return;
    element.value = state.fields[field].value;
    if (state.fields[field].specificCardText === undefined) {
      delete element.dataset.specificCardText;
    } else {
      element.dataset.specificCardText = state.fields[field].specificCardText;
    }
  });
  var specific = document.getElementById('deck_input_specific_cards');
  if (specific && state.specificCardsChecked !== null) specific.checked = state.specificCardsChecked;
}

export function sortOptions(options, sortType, sortDirection) {
  var withIndex = options.map(function(option, index) { return { option: option, index: index }; });
  var direction = sortDirection === 'auto' ? (sortType === 'release-date' ? 'desc' : 'asc') : sortDirection;
  var sortScalar = function(leftValue, rightValue) {
    if (leftValue === null && rightValue === null) return 0;
    if (leftValue === null) return 1;
    if (rightValue === null) return -1;
    return direction === 'desc' ? rightValue - leftValue : leftValue - rightValue;
  };
  if (sortType === 'paper-price' || sortType === 'online-price') {
    var priceField = sortType === 'online-price' ? 'online_price' : 'price';
    withIndex.sort(function(left, right) {
      var compared = sortScalar(priceValue(left.option[priceField]), priceValue(right.option[priceField]));
      return compared || left.index - right.index;
    });
  } else if (sortType === 'release-date') {
    withIndex.sort(function(left, right) {
      var leftDate = Date.parse(left.option.release_date || '') || 0;
      var rightDate = Date.parse(right.option.release_date || '') || 0;
      var compared = sortScalar(leftDate || null, rightDate || null);
      return compared || left.index - right.index;
    });
  }
  return withIndex.map(function(item) { return item.option; });
}

function quantityLabel(entry) {
  return entry.quantity + 'x';
}

function optionTags(option) {
  return [option.set, option.collector_number && '#' + option.collector_number, option.finish_label, ...(option.tags || [])].filter(Boolean).join(' | ');
}

function optionTitle(option) {
  var title = option.display_name || option.display_identity || '';
  var tags = option.tags || [];
  if (tags.length) {
    var duplicatedTags = tags.join(', ');
    title = title.replace(new RegExp('\\s*\\(' + escapeRegExp(duplicatedTags) + '\\)$'), '');
  }
  return title;
}

function priceLabel(option, sortType) {
  var priceType = sortType === 'online-price' ? 'online' : 'paper';
  var value = priceType === 'online' ? option.online_price : option.price;
  return formattedPrice(value, priceType, 'No price');
}

function formattedPrice(value, type, nullPriceValue) {
  var price = priceValue(value);
  if (price === null) return nullPriceValue || '';
  return format_price_with_currency(String(price), type, 2, nullPriceValue || null);
}

function priceValue(value) {
  if (value === null || value === undefined) return null;
  var normalized = String(value).trim();
  if (normalized === '') return null;
  var price = Number(normalized);
  return Number.isFinite(price) ? price : null;
}

document.addEventListener('DOMContentLoaded', function() {
  var launcher = document.getElementById('deck-printing-picker-launch');
  if (document.getElementById('deck-printing-picker')) {
    window.MTGGoldfish = window.MTGGoldfish || {};
    window.MTGGoldfish.deckPrintingPicker = new DeckPrintingPicker(launcher);
  }
});

function previewRowTarget(row) {
  return {
    sourceField: sourceFieldForBoard(row.dataset.editBoard),
    cardName: normalizeCardName(row.dataset.editCard || ''),
    source: normalizeSourceLine(row.dataset.editSource || ''),
    cardUuid: row.dataset.cardUuid || ''
  };
}

function payloadForPreviewTarget(payload, target) {
  var entries = payload.entries.filter(function(entry) { return entryMatchesPreviewTarget(entry, target); });
  if (!entries.length) throw new Error('No matching deck entry was found for that preview card. Update Preview, then try again.');

  var optionUuids = new Set();
  entries.forEach(function(entry) {
    (entry.option_uuids || []).forEach(function(uuid) { optionUuids.add(uuid); });
  });
  var options = {};
  Object.keys(payload.options || {}).forEach(function(uuid) {
    if (optionUuids.has(uuid)) options[uuid] = payload.options[uuid];
  });

  return Object.assign({}, payload, { entries: entries, options: options });
}

function entryMatchesPreviewTarget(entry, target) {
  if (entry.source_field !== target.sourceField) return false;
  if (target.source && normalizeSourceLine(entry.source || '') === target.source) return true;
  if (target.cardUuid && (entry.current_uuid === target.cardUuid || (entry.option_uuids || []).indexOf(target.cardUuid) !== -1)) return true;
  if (target.source && normalizedSourceCardName(entry.source || entry.display_name || '') === normalizedSourceCardName(target.source)) return true;
  return normalizeCardName(entry.display_name || entry.source || '') === target.cardName;
}

function sourceFieldForBoard(board) {
  if (board === 'sideboard') return 'sideboard';
  if (board === 'maybeboard') return 'maybeboard';
  return 'deck';
}

function normalizeCardName(name) {
  return name.toString().replace(/\s+/g, ' ').trim().toLowerCase();
}

function normalizeSourceLine(source) {
  return source.toString().replace(/\s+/g, ' ').trim().toLowerCase();
}

function normalizedSourceCardName(source) {
  var normalized = normalizeSourceLine(source).replace(/^\d+x?\s+/, '');
  normalized = normalized.replace(/\s+\[[^\]]+\]\s*(?:\([^)]*\)\s*)*$/, '');
  while (/\s+\([^)]*\)\s*$/.test(normalized)) {
    normalized = normalized.replace(/\s+\([^)]*\)\s*$/, '');
  }
  return normalized.trim();
}

function escapeRegExp(value) {
  return value.toString().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function setPopoverAttributes(element, option) {
  if (!option?.image_url) return;

  element.setAttribute('rel', 'popover');
  element.setAttribute('data-full-image', option.image_url);
  element.setAttribute('data-card-id', option.storage_identity || option.display_identity || option.display_name || '');
}
